import axios from "axios";
import { Fragment, useEffect, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { impersonate } from "../../../utils/impersonate";
import Footer from "../../layout/Footer";
import Loader from "../../layout/Loader";
import Menu from "../../layout/Menu";
import ExportUserModal from "./ExportUserModal";
import { buildUserListRequest } from "./userExport";
import { formatDate, formatPhoneNumber, USER_TYPE_MAPPING } from "../../../constants";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [displayUsers, setDisplayUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const rows = [20, 50, 100];
  const [userPerPage, setUserPerPage] = useState(rows[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [activeTab, setActiveTab] = useState("active"); // "active" or "inactive"
  const [searchTerm, setSearchTerm] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("ALL");
  const [companyFilter, setCompanyFilter] = useState("ALL");
  const [exportModal, setExportModal] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    document.getElementById("page-loader").style.display = "none";
    var element = document.getElementById("page-container");
    element.classList.add("show");
    getCompanies();
  }, []);

  useEffect(() => {
    getUserData();
  }, [activeTab, currentPage, userPerPage, searchTerm, userTypeFilter, companyFilter]);

  const getCompanies = async () => {
    try {
      const myurl = `${process.env.REACT_APP_base_url}api/masterAdmin/companies-dropdown`;
      const requestData = {
        auth_code: process.env.REACT_APP_AUTH_CODE,
      };

      const response = await axios.post(myurl, requestData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success === true) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Failed to fetch companies");
    }
  };

  const getUserData = async () => {
    try {
      setLoader(true);

      // Shared with the CSV export so the two can never diverge on
      // endpoint or params — see ./userExport.js
      const { url: myurl, requestData } = buildUserListRequest({
        tab: activeTab,
        page: currentPage,
        limit: userPerPage,
        searchTerm,
        userType: userTypeFilter,
        company: companyFilter,
      });

      const response = await axios.post(myurl, requestData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success === true) {
        const { data, pagination } = response.data;
        const arr = data.map((elem, index) => {
          const globalIndex = (currentPage - 1) * userPerPage + index + 1;
          return { ...elem, indexs: globalIndex };
        });

        setUsers(arr);
        setDisplayUsers(arr);
        setTotalPages(pagination?.totalPages || 0);
        setTotalCount(pagination?.totalCount || 0);
      } else {
        toast.error(response.data.message || "Failed to fetch users data");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error(error.response?.data?.message || "Failed to fetch users data");
    } finally {
      setLoader(false);
    }
  };

  const handleUserStatusChange = async (user) => {
    try {
      const myurl = `${process.env.REACT_APP_base_url}api/masterAdmin/user-status-update`;
      const requestData = {
        auth_code: process.env.REACT_APP_AUTH_CODE,
        user_id: user._id || user.id,
      };

      const response = await axios.post(myurl, requestData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success === true) {
        // Refresh the data instead of manual state update
        getUserData();
        toast.success("User status updated successfully!");
      } else {
        toast.error(response.data.message || "Failed to update user status");
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error(error.response?.data?.message || "Failed to update user status");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when changing tabs
    setSearchTerm(""); // Clear search when switching tabs
    setUserTypeFilter("ALL"); // Reset filters when switching tabs
    setCompanyFilter("ALL");
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleUserTypeFilter = (e) => {
    setUserTypeFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleCompanyFilter = (e) => {
    setCompanyFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const paginate = (num) => {
    setCurrentPage(num);
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    if (currentPage > 1) {
      pageNumbers.push(
        <li key="prev" className="page-item">
          <span className="page-link" onClick={() => paginate(currentPage - 1)} style={{ cursor: "pointer" }}>
            Previous
          </span>
        </li>
      );
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li key={i} className={currentPage === i ? "page-item active" : "page-item"}>
          <span className="page-link" onClick={() => paginate(i)} style={{ cursor: "pointer" }}>
            {i}
          </span>
        </li>
      );
    }

    // Next button
    if (currentPage < totalPages) {
      pageNumbers.push(
        <li key="next" className="page-item">
          <span className="page-link" onClick={() => paginate(currentPage + 1)} style={{ cursor: "pointer" }}>
            Next
          </span>
        </li>
      );
    }

    return pageNumbers;
  };

  const getUserTypeLabel = (type) => {
    return USER_TYPE_MAPPING[type] || "Unknown";
  };

  const handleCompanyUserLogin = (companyId, userId) => {
    impersonate({
      endpoint: "company-user-login",
      params: { company: companyId, userId },
      baseUrl: process.env.REACT_APP_COMPANY_USER_URL,
    });
  };

  return (
    <Fragment>
      <Loader />
      <div
        id="page-container"
        className="fade page-sidebar-fixed page-header-fixed"
      >
        <Menu />
        <ExportUserModal
          activeModal={exportModal}
          setActiveModal={() => setExportModal(false)}
          defaultTab={activeTab}
          searchTerm={searchTerm}
          userTypeFilter={userTypeFilter}
          companyFilter={companyFilter}
          companies={companies}
          pageScope={{
            tab: activeTab,
            currentPage,
            pageSize: userPerPage,
            totalPages,
            totalCount,
          }}
        />
        <div id="content" className="content">
          <ol className="breadcrumb float-xl-right">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Users</li>
          </ol>
          <h1 className="page-header">Users</h1>

          {/* Tab Navigation */}
          <div className="card">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "active" ? "active" : ""}`}
                    onClick={() => handleTabChange("active")}
                    style={{
                      border: "none",
                      background: "none",
                      color: activeTab === "active" ? "#007bff" : "#6c757d"
                    }}
                  >
                    Active Users ({activeTab === "active" ? totalCount : "-"})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "inactive" ? "active" : ""}`}
                    onClick={() => handleTabChange("inactive")}
                    style={{
                      border: "none",
                      background: "none",
                      color: activeTab === "inactive" ? "#007bff" : "#6c757d"
                    }}
                  >
                    Inactive Users ({activeTab === "inactive" ? totalCount : "-"})
                  </button>
                </li>
              </ul>
            </div>

            <div className="card-body">
              {/* Filters */}
              <div className="form-outline mb-4" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <select
                  className="form-select"
                  onChange={handleUserTypeFilter}
                  value={userTypeFilter}
                  style={{
                    padding: "5px 10px",
                    border: "1px solid #d5dbe0",
                    borderRadius: "4px",
                    minWidth: "120px"
                  }}
                >
                  <option value="ALL">All User Types</option>
                  {Object.entries(USER_TYPE_MAPPING).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>

                <select
                  className="form-select"
                  onChange={handleCompanyFilter}
                  value={companyFilter}
                  style={{
                    padding: "5px 10px",
                    border: "1px solid #d5dbe0",
                    borderRadius: "4px",
                    minWidth: "150px"
                  }}
                >
                  <option value="ALL">All Companies</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>

                {/* Grouped so the Export button always sits on the search box's
                    row — .form-control is width:100%, which would otherwise make
                    the input claim the whole line and wrap the button below it. */}
                <div style={{ display: "flex", gap: "10px", flex: "1 1 320px" }}>
                  <input
                    type="search"
                    className="form-control"
                    id="datatable-search-input"
                    placeholder="Search User"
                    value={searchTerm}
                    onChange={handleSearch}
                    style={{ minWidth: "200px" }}
                  />

                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                    onClick={() => setExportModal(true)}
                    disabled={loader}
                    title="Export users to CSV"
                  >
                    <i className="fa fa-download me-2 mr-2"></i>
                    Export
                  </button>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <div className="table-responsive">
                    <table id="product-listing" className="table">
                      <thead>
                        <tr>
                          <th>Sr. No.</th>
                          <th>User Info</th>
                          <th>Phone No</th>
                          <th>Company</th>
                          <th>User Type</th>
                          <th>Status</th>
                          <th style={{ whiteSpace: "nowrap" }}>Created At</th>
                          <th style={{ whiteSpace: "nowrap" }}>Deactivated At</th>
                          <th style={{ whiteSpace: "nowrap" }}>Reactivated At</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loader ? (
                          <tr className="text-center">
                            <td colSpan={10}>
                              <ThreeDots
                                height="35"
                                width="35"
                                radius="9"
                                color="rgba(0, 0, 0, .4)"
                                ariaLabel="three-dots-loading"
                                wrapperStyle={{ justifyContent: "center" }}
                                visible={true}
                              />
                            </td>
                          </tr>
                        ) : displayUsers.length > 0 ? (
                          displayUsers.map((elem, index) => (
                            <tr key={index}>
                              <td>{elem.indexs}</td>
                              <td>
                                <div>
                                  <strong>{elem.username}</strong>
                                  <br />
                                  <small className="text-muted">{elem.email}</small>
                                </div>
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatPhoneNumber(elem?.countryCode, elem?.phoneNo)}
                              </td>
                              <td>{elem.company}</td>
                              <td>{getUserTypeLabel(elem.type)}</td>
                              <td>
                                {elem.status === 1 ? (
                                  <span
                                    className="status"
                                    style={{ backgroundColor: "green" }}
                                  >
                                    Active
                                  </span>
                                ) : (
                                  <span
                                    className="status"
                                    style={{ backgroundColor: "orange" }}
                                  >
                                    Inactive
                                  </span>
                                )}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>{formatDate(elem?.createdAt)}</td>
                              <td style={{ whiteSpace: "nowrap" }}>{formatDate(elem?.deactivatedAt)}</td>
                              <td style={{ whiteSpace: "nowrap" }}>{formatDate(elem.reactivatedAt)}</td>
                              <td>
                                {elem.status === 1 && (
                                  <i
                                    className="fa fa-user"
                                    style={{
                                      color: "gray",
                                      cursor: "pointer",
                                      fontSize: "16px",
                                      marginRight: "12px",
                                    }}
                                    title="User Panel"
                                    onClick={() =>
                                      handleCompanyUserLogin(elem.companyId, elem._id)
                                    }
                                  ></i>
                                )}
                                {elem.status === 1 ? (
                                  <i
                                    className="fa fa-toggle-on"
                                    style={{
                                      color: "green",
                                      cursor: "pointer",
                                      fontSize: "25px",
                                    }}
                                    onClick={() => handleUserStatusChange(elem)}
                                    title="Deactivate User"
                                  ></i>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 576 512"
                                    height={25}
                                    fill="orange"
                                    onClick={() => handleUserStatusChange(elem)}
                                    style={{ cursor: "pointer" }}
                                    title="Activate User"
                                  >
                                    <path d="M384 128c70.7 0 128 57.3 128 128s-57.3 128-128 128H192c-70.7 0-128-57.3-128-128s57.3-128 128-128H384zM576 256c0-106-86-192-192-192H192C86 64 0 150 0 256S86 448 192 448H384c106 0 192-86 192-192zM192 352a96 96 0 1 0 0-192 96 96 0 1 0 0 192z" />
                                  </svg>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className="text-center">
                            <td colSpan={10}>No Record Found..</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="gt-pagination" style={{ display: "flex", justifyContent: "space-between" }}>
                    <ul className="pagination">
                      {renderPagination()}
                    </ul>
                    <div className="pagination-info" style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                      <span style={{ fontSize: "14px", color: "#6c757d" }}>
                        Showing {((currentPage - 1) * userPerPage) + 1} to {Math.min(currentPage * userPerPage, totalCount)} of {totalCount} entries
                      </span>
                      <div
                        className="filter-pages"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <label htmlFor="selection" style={{ marginBottom: "0" }}>
                          Users Per Page :
                        </label>
                        <select
                          className="selection"
                          style={{
                            outline: "0",
                            borderWidth: "0 0 1px",
                            borderColor: "black",
                            marginLeft: "10px",
                          }}
                          value={userPerPage}
                          onChange={(e) => {
                            setUserPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                        >
                          {rows.map((value, key) => (
                            <option value={value} key={key}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </Fragment>
  );
};

export default Users;